/**
 * Defensive shaping and formatting for API payloads.
 *
 * The public /api/products response redacts source_price/source_url, and orders
 * are persisted as `total_price` while older documents use `total`. Reading
 * those fields directly threw TypeErrors during render, which unmounted the
 * whole React tree and left the admin on a blank page.
 */

export const CURRENCY_SYMBOLS = {
  USD: "$",
  BBD: "BBD $",
  CAD: "CAD $",
  GBP: "£",
  TTD: "TTD $",
  JMD: "JMD $",
  EUR: "€",
};

export const ORDER_STATUS_KEYS = [
  "pending_payment",
  "paid",
  "processing",
  "shipped",
  "delivered",
  "cancelled",
];

export const ORDER_STATUS_LABELS = {
  pending_payment: "Pending Payment",
  paid: "Paid",
  processing: "Processing",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

// Backend order lifecycle constants mapped onto the vocabulary the admin
// dropdown can actually PUT back to /admin/orders/{id}/status.
const ORDER_STATUS_ALIASES = {
  unpaid: "pending_payment",
  pending: "pending_payment",
  pending_verification: "processing",
  paid_locked: "paid",
  fulfilled: "delivered",
  rejected: "cancelled",
};

export function toNumber(value, fallback = null) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : fallback;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

export function toText(value, fallback = "") {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return fallback;
}

export function toList(value) {
  return Array.isArray(value) ? value : [];
}

/** Accepts `{ products: [...] }`, a bare array, or anything broken. */
export function extractCollection(payload, key) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload[key])) return payload[key];
  return [];
}

export function formatMoney(amount, currency = "USD", placeholder = "—") {
  const value = toNumber(amount);
  if (value === null) return placeholder;
  const symbol = CURRENCY_SYMBOLS[currency] || CURRENCY_SYMBOLS.USD;
  return `${symbol}${value.toFixed(2)}`;
}

export function formatDateTime(value, placeholder = "—") {
  if (!value) return placeholder;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return placeholder;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function canonicalOrderStatus(rawStatus) {
  const raw = toText(rawStatus).trim();
  if (!raw) return "";
  const lower = raw.toLowerCase();
  if (ORDER_STATUS_KEYS.includes(lower)) return lower;
  return ORDER_STATUS_ALIASES[lower] || lower;
}

export function humanizeStatus(rawStatus) {
  const key = canonicalOrderStatus(rawStatus);
  if (!key) return "Unknown";
  if (ORDER_STATUS_LABELS[key]) return ORDER_STATUS_LABELS[key];
  return key
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function normalizeAdminProduct(raw) {
  const product = raw && typeof raw === "object" ? raw : {};
  return {
    ...product,
    product_id: toText(product.product_id ?? product.id),
    name: toText(product.name ?? product.override_title ?? product.title, "Untitled product"),
    description: toText(product.description),
    marketplace: toText(product.marketplace ?? product.source_platform),
    category: toText(product.category),
    source_url: toText(product.source_url),
    source_currency: toText(product.source_currency, "USD"),
    // Redacted by the public endpoint — stays null instead of crashing toFixed.
    source_price: toNumber(product.source_price ?? product.base_price ?? product.price),
    landed_price: toNumber(product.landed_price ?? product.mansion_certified_price),
    weight_lbs: toNumber(product.weight_lbs),
    estimated_weight_lbs: toNumber(product.estimated_weight_lbs),
    images: toList(product.images).filter((url) => typeof url === "string" && url.trim()),
    profit_margin: toNumber(product.profit_margin),
    ...normalizeProductCatalogState(product),
  };
}

/**
 * Products carry both `status` (admin label) and `in_stock` (what the public
 * catalog filters on), and older rows have only one of them. Read whichever is
 * present so a row is never shown as sellable when it is not.
 */
function normalizeProductCatalogState(product) {
  const rawStatus = toText(product.status).trim().toLowerCase();
  const inStockFlag = product.in_stock !== false;

  if (rawStatus && rawStatus !== "active") {
    return { status: rawStatus, in_stock: false };
  }
  if (!inStockFlag) {
    return { status: "inactive", in_stock: false };
  }
  return { status: "active", in_stock: true };
}

export function normalizeAdminProducts(payload) {
  return extractCollection(payload, "products").map(normalizeAdminProduct);
}

export function normalizeAdminOrder(raw) {
  const order = raw && typeof raw === "object" ? raw : {};
  const address = order.shipping_address && typeof order.shipping_address === "object"
    ? order.shipping_address
    : {};
  return {
    ...order,
    order_id: toText(order.order_id ?? order.id),
    status: toText(order.status),
    status_key: canonicalOrderStatus(order.status),
    total: toNumber(order.total ?? order.total_price ?? order.total_amount),
    currency: toText(order.currency, "USD"),
    destination: toText(order.destination, "—"),
    items: toList(order.items),
    created_at: toText(order.created_at),
    customer_name: toText(address.name ?? order.customer_name),
    customer_email: toText(address.email ?? order.customer_email),
  };
}

export function normalizeAdminOrders(payload) {
  return extractCollection(payload, "orders").map(normalizeAdminOrder);
}

/** Case-insensitive "does any of these fields contain the query" test. */
export function matchesSearch(fields, query) {
  const needle = toText(query).trim().toLowerCase();
  if (!needle) return true;
  return toList(fields).some((field) => toText(field).toLowerCase().includes(needle));
}

export function buildCheckoutLink(origin, orderId) {
  const id = toText(orderId).trim();
  if (!id) return "";
  const base = toText(origin).replace(/\/+$/, "");
  return `${base}/checkout?order_id=${encodeURIComponent(id)}`;
}

export function extractApiErrorMessage(error, fallback = "Request failed") {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string" && detail.message.trim()) return detail.message;
    if (Array.isArray(detail.errors) && detail.errors.length) {
      return detail.errors.map((entry) => toText(entry?.msg ?? entry)).join("; ");
    }
  }
  if (Array.isArray(detail) && detail.length) {
    return detail.map((entry) => toText(entry?.msg ?? entry)).join("; ");
  }
  if (typeof error?.message === "string" && error.message.trim()) return error.message;
  return fallback;
}
